import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { AppStoreAvailableBuild, AppStoreType, IntegrationType, PhasedRelease, SubscriptionFeatureType } from '@/types';
import Space from 'antd/lib/space';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Progress from 'antd/lib/progress';
import Tooltip from 'antd/lib/tooltip';
import { getDayPairsFromDaysToPercent, isPhasedReleasePaused } from '@/helpers';
import { doesPercentageMatchDayProgression } from '@/typescriptHelpers';
import { displayTextForPhasedReleaseState, phasedReleaseDisplayTextForAppStoreType } from '@/helpers/displayHelpers';
import { colorForPhasedState } from '@/components/monitoring/PhasedReleaseCard';
import { ThunderboltFilled } from '@ant-design/icons';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';

export interface LiveReleaseChartProps {
    phasedRelease?: PhasedRelease;
    appStoreIntegrationId?: IntegrationType;
    stabIntegrationId?: IntegrationType;
    selectedBuild?: AppStoreAvailableBuild;
    daysToPercent?: number[];
    appStoreType?: AppStoreType;
    isAutomatedPhasedRollout: boolean;
}

export const LiveReleaseChartWidget: React.FC<LiveReleaseChartProps> = ({
    phasedRelease,
    appStoreIntegrationId,
    stabIntegrationId,
    selectedBuild,
    appStoreType,
    daysToPercent,
    isAutomatedPhasedRollout,
}) => {
    const [appStoreIcon, appStoreAlt] = iconAndAltForIntegrationType(appStoreIntegrationId);
    const [stabIcon, stabAlt] = iconAndAltForIntegrationType(stabIntegrationId);

    const sessionAdoption = selectedBuild?.stabilityInfo?.sessionAdoption;

    const wasAccelerated =
        phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout;
    const wasHalted = isPhasedReleasePaused(phasedRelease?.state);

    const statusString = displayTextForPhasedReleaseState(
        phasedRelease?.state,
        phasedRelease?.isManuallyAcceleratedPhasedRollout,
        phasedRelease?.isRunwayAcceleratedRollout,
        phasedRelease?.isRunwayPaused,
    );

    const statusColor = colorForPhasedState(phasedRelease?.state);

    if (!phasedRelease && !sessionAdoption) {
        return null;
    }

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        borderRadius: 4,
                        backgroundColor: antGrey.grey1,
                        border: `1px solid ${antGrey.grey5}`,
                        flex: 1,
                        padding: '16px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'content-box',
                        alignItems: 'stretch',
                        gap: 20,
                        marginBottom: 8,
                    }}
                >
                    {phasedRelease && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                gap: 8,
                                alignItems: 'stretch',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Space
                                    split={
                                        <div
                                            style={{
                                                width: 1,
                                                height: 10,
                                                backgroundColor: antGrey.grey5,
                                            }}
                                        />
                                    }
                                    size={8}
                                >
                                    <Space>
                                        {appStoreIcon && (
                                            <img src={appStoreIcon} alt={appStoreAlt} width={16} height={16} />
                                        )}
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                            }}
                                        >
                                            {phasedReleaseDisplayTextForAppStoreType(appStoreType)}
                                        </Text>
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                            mini
                                        />
                                    </Space>
                                    {(wasAccelerated || wasHalted) && (
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                                color: statusColor,
                                            }}
                                        >
                                            {statusString}
                                        </Text>
                                    )}
                                </Space>
                                {!isDisabled && (
                                    <PhasedReleaseDaysSummary
                                        percentage={phasedRelease.phasedReleasePercent}
                                        dayNumber={phasedRelease.dayNumber}
                                        daysToPercent={daysToPercent}
                                        isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                        isRunwayAcceleratedRollout={phasedRelease.isManuallyAcceleratedPhasedRollout}
                                        isRunwayRolloutPaused={phasedRelease.isRunwayPaused}
                                    />
                                )}
                            </div>
                            {isDisabled ?
                                <Progress
                                    className="widget-progress-bar"
                                    strokeLinecap="butt"
                                    showInfo={false}
                                    strokeColor={runwayColors.primary}
                                    percent={0}
                                    trailColor={antGrey.grey4}
                                    style={{
                                        flexGrow: 1,
                                        marginBottom: 0,
                                    }}
                                />
                            :   <PhasedReleaseChartBase
                                    percentage={phasedRelease.phasedReleasePercent}
                                    dayNumber={phasedRelease.dayNumber}
                                    daysToPercent={daysToPercent}
                                    isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                    wasHalted={isPhasedReleasePaused(phasedRelease.state)}
                                    wasAccelerated={
                                        phasedRelease?.isManuallyAcceleratedPhasedRollout ||
                                        phasedRelease?.isRunwayAcceleratedRollout
                                    }
                                />
                            }
                        </div>
                    )}

                    {sessionAdoption && (
                        <AdoptionRateWidget
                            sessionAdoption={sessionAdoption}
                            stabIcon={stabIcon}
                            stabAlt={stabAlt}
                            isDisabled={isDisabled}
                        />
                    )}
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export interface PhasedReleaseDaysSummaryProps {
    percentage: number;
    dayNumber: number;
    daysToPercent?: number[];
    isAutomatedPhasedRollout: boolean;
    isRunwayAcceleratedRollout: boolean;
    isRunwayRolloutPaused: boolean;
}

export const PhasedReleaseDaysSummary: React.FC<PhasedReleaseDaysSummaryProps> = ({
    percentage,
    dayNumber,
    daysToPercent,
    isAutomatedPhasedRollout,
    isRunwayAcceleratedRollout,
    isRunwayRolloutPaused,
}) => {
    const dayPairs = !!daysToPercent && daysToPercent.length > 0 ? getDayPairsFromDaysToPercent(daysToPercent) : null;
    const isAutomatedAndHaveDayPairs = isAutomatedPhasedRollout && !!dayPairs;

    const percentageMatchesDayNumbersProgression = doesPercentageMatchDayProgression(percentage, dayNumber, dayPairs);

    return (
        <Space>
            {(isRunwayAcceleratedRollout || isRunwayRolloutPaused) && (
                <ThunderboltFilled style={{ color: runwayColors.primary, fontSize: 10 }} />
            )}
            <Text
                ellipsis
                style={{
                    ...appHomeCardsWidgetTitleStyle,
                }}
            >
                {!percentageMatchesDayNumbersProgression ?
                    `${percentage}%`
                : isAutomatedAndHaveDayPairs ?
                    `Day ${dayNumber} of ${dayPairs.length} — ${percentage}%`
                :   `Day ${dayNumber} — ${percentage}%`}
            </Text>
        </Space>
    );
};

export interface PhasedReleaseChartBaseProps {
    percentage: number;
    dayNumber: number;
    daysToPercent?: number[];
    isAutomatedPhasedRollout: boolean;
    wasHalted: boolean;
    wasAccelerated: boolean;
    compact?: boolean;
}

export const PhasedReleaseChartBase: React.FC<PhasedReleaseChartBaseProps> = ({
    percentage,
    dayNumber,
    daysToPercent,
    isAutomatedPhasedRollout,
    wasHalted,
    wasAccelerated,
    compact = false,
}) => {
    const dayPairs = !!daysToPercent && daysToPercent.length > 0 ? getDayPairsFromDaysToPercent(daysToPercent) : null;
    const isAutomatedAndHaveDayPairs = isAutomatedPhasedRollout && !!dayPairs;

    const percentageMatchesDayNumbersProgression = doesPercentageMatchDayProgression(percentage, dayNumber, dayPairs);

    if (compact) {
        const isCompleted = percentage === 100;
        const filledDays = dayNumber;

        return (
            <Space size={8} style={{ whiteSpace: 'nowrap', alignItems: 'center' }}>
                <Text style={{ color: antGrey.grey7 }}>Day {dayNumber}</Text>
                <div
                    style={{
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                        height: 8,
                        overflow: 'hidden',
                    }}
                >
                    {(dayPairs ?? []).slice(0, filledDays).map(([day, percent], index) => {
                        const isLastFilledDay = index === dayNumber - 1;
                        const isHaltedDay = index + 1 === dayNumber && wasHalted;
                        const isAnyDayBeforeHalted = index + 1 < dayNumber && wasHalted;
                        const tooltipText =
                            isCompleted && isLastFilledDay ? `Day ${day} — ${percentage}%` : `Day ${day} — ${percent}%`;

                        return (
                            <Tooltip key={day} title={tooltipText}>
                                <div
                                    style={{
                                        backgroundColor: runwayColors.primary,
                                        height: 8,
                                        width: 8,
                                        borderRadius: 2,
                                        ...(isAnyDayBeforeHalted && {
                                            backgroundColor: runwayColors.statusRed,
                                            opacity: 0.7,
                                        }),
                                        ...(isHaltedDay && {
                                            backgroundColor: runwayColors.statusRed,
                                            opacity: 1,
                                        }),
                                        ...(wasAccelerated && {
                                            backgroundColor: runwayColors.statusGreen,
                                            opacity: 0.7,
                                        }),
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </div>
                <Text style={{ color: antGrey.grey7 }}>{percentage}%</Text>
                <div
                    style={{
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                        height: 8,
                        overflow: 'hidden',
                    }}
                >
                    {(dayPairs ?? []).slice(filledDays).map(([day, percent]) => {
                        const isAcceleratedDay = wasAccelerated;
                        return (
                            <Tooltip key={day} title={`Day ${day} — ${percent}%`}>
                                <div
                                    style={{
                                        backgroundColor: antGrey.grey5,
                                        height: 8,
                                        width: 8,
                                        borderRadius: 2,
                                        ...(isAcceleratedDay && {
                                            backgroundColor: runwayColors.statusGreen,
                                            opacity: 1,
                                        }),
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </div>
            </Space>
        );
    }

    return isAutomatedAndHaveDayPairs && percentageMatchesDayNumbersProgression ?
            <div
                style={{
                    display: 'flex',
                    gap: 4,
                    alignItems: 'center',
                    width: '100%',
                    height: 8,
                    borderRadius: 8,
                    overflow: 'hidden',
                }}
            >
                {dayPairs.map(([day, percent], index) => {
                    const isFilled = index < dayNumber;
                    const isHaltedDay = index + 1 === dayNumber && wasHalted;
                    const isAnyDayBeforeHalted = index + 1 < dayNumber && wasHalted;
                    const isAcceleratedDay = index + 1 > dayNumber && wasAccelerated;
                    return (
                        <Tooltip key={day} title={`Day ${day} — ${percent}%`}>
                            <div
                                style={{
                                    backgroundColor: isFilled ? runwayColors.primary : antGrey.grey4,
                                    height: 8,
                                    flex: Math.max(percent, 3),
                                    ...(isAnyDayBeforeHalted && {
                                        backgroundColor: runwayColors.statusRed,
                                        opacity: 0.7,
                                    }),
                                    ...(isHaltedDay && {
                                        backgroundColor: runwayColors.statusRed,
                                        opacity: 1,
                                    }),
                                    ...(wasAccelerated && {
                                        backgroundColor: runwayColors.statusGreen,
                                        opacity: 0.7,
                                    }),
                                    ...(isAcceleratedDay && {
                                        backgroundColor: runwayColors.statusGreen,
                                        opacity: 1,
                                    }),
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </div>
        :   <Progress
                className="widget-progress-bar"
                strokeLinecap="round"
                percent={percentage}
                showInfo={false}
                strokeColor={
                    wasHalted ? runwayColors.statusRed
                    : wasAccelerated ?
                        runwayColors.statusGreen
                    :   runwayColors.primary
                }
                trailColor={antGrey.grey4}
                style={{
                    flexGrow: 1,
                    margin: 0,
                }}
                size={['100%', 8]}
            />;
};

export interface AdoptionRateWidgetProps {
    sessionAdoption: number;
    stabIcon?: string;
    stabAlt?: string;
    isDisabled?: boolean;
    compact?: boolean;
}

export const AdoptionRateWidget: React.FC<AdoptionRateWidgetProps> = ({
    sessionAdoption,
    stabIcon,
    stabAlt,
    isDisabled = false,
    compact = false,
}) => {
    if (compact) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                }}
            >
                {stabIcon && <img src={stabIcon} alt={stabAlt} width={16} height={16} />}
                <Progress
                    className="widget-progress-bar"
                    strokeLinecap="butt"
                    percent={sessionAdoption}
                    showInfo={false}
                    strokeColor={runwayColors.primary}
                    trailColor={antGrey.grey4}
                    style={{
                        flexGrow: 1,
                        marginBottom: 0,
                    }}
                />
                <Text style={{ color: antGrey.grey7, wordBreak: 'keep-all' }}>{sessionAdoption.toFixed(0)}%</Text>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: 8,
                alignItems: 'stretch',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Space>
                    {stabIcon && <img src={stabIcon} alt={stabAlt} width={16} height={16} />}
                    <Text
                        style={{
                            ...appHomeCardsWidgetTitleStyle,
                        }}
                    >
                        Adoption rate (sessions)
                    </Text>
                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip mini />
                </Space>
                {!isDisabled && (
                    <Text
                        style={{
                            ...appHomeCardsWidgetTitleStyle,
                        }}
                    >
                        {sessionAdoption.toFixed(0)}%
                    </Text>
                )}
            </div>
            <Progress
                className="widget-progress-bar"
                strokeLinecap="butt"
                percent={isDisabled ? 0 : sessionAdoption}
                showInfo={false}
                strokeColor={runwayColors.primary}
                trailColor={antGrey.grey4}
                style={{
                    flexGrow: 1,
                    marginBottom: 0,
                }}
            />
        </div>
    );
};
